import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormGroupDirective, NgForm, Validators } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Product } from '../models/product';
import { Sponsor } from '../models/sponsor';
import { Voucher } from '../models/voucher';
import { AuthService } from '../services/auth.service';
import { MemberService } from '../services/member.service';
import { ProductService } from '../services/product.service';
import { SponsorService } from '../services/sponsor.service';
import { VoucherService } from '../services/voucher.service';
import { ConfirmComponent } from '../shared/dialogs/confirm/confirm.component';
import { SnackBarIconService } from '../shared/snack-bar-icon/snack-bar-icon.service';
import { VoucherDialogComponent } from './dialogs/voucher-dialog/voucher-dialog.component';

/** Error when invalid control is dirty, touched, or submitted. */
export class FormErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

@Component({
  selector: 'app-voucher',
  templateUrl: './voucher.component.html',
  styleUrls: ['./voucher.component.scss']
})
export class VoucherComponent implements OnInit {

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  public spinner: boolean = false;
  public matcher = new FormErrorStateMatcher();

  public entityForm: FormGroup;
  public current_sponsor: Sponsor;
  public current_product: Product;

  public sponsors: Sponsor[] = []
  public products: Product[] = []
  public vouchers: Voucher[] = []

  public dataSource: MatTableDataSource<any>
  public displayedColumns: string[] = ['icon', 'name', 'discount', 'sponsor', 'product'];

  public hasList: boolean = false;

  public voucherDataSource: MatTableDataSource<any>
  public displayedVoucherColumns: string[] = ['icon', 'name', 'discount', 'member', 'email', 'sent', 'used', 'edit', 'delete'];

  constructor(
    public _productService: ProductService,
    private _memberService: MemberService,
    private fb: FormBuilder,
    private _sponsorService: SponsorService,
    private _voucherService: VoucherService,
    private dialog: MatDialog,
    private snackBar: SnackBarIconService,
    private _authService: AuthService,
    private router: Router
  ) {
    if (!this._authService.isAdmin) {
      this.router.navigate(['home']);
    }

    this.loadSponsors();
    this.loadProducts();
  }

  ngOnInit() {
    this.entityForm = this.createForm();
  }

  loadSponsors() {

    this._sponsorService.select()
      .subscribe((result) => {
        this.sponsors = result.map(item => {
          let entity = new Sponsor();
          entity.id = item.payload.doc.id;
          entity.name = item.payload.doc.data().name;
          entity.url = item.payload.doc.data().url;
          entity.image = item.payload.doc.data().image;
          return entity;
        });

      })

  }

  loadProducts() {
    this._productService.selectProducts()
      .subscribe((result) => {
        this.products = result.map(item => {
          let entity = new Product();
          entity.id = item.payload.doc.id;
          entity.name = item.payload.doc.data().name;
          entity.image = item.payload.doc.data().image;
          return entity;
        });

      })

  }

  createVoucherForm() {
    let form: FormGroup = this.fb.group({

      id: new FormControl(''),
      name: new FormControl(''),

      sponsor_id: new FormControl(''),
      sponsor_name: new FormControl(''),

      member_id: new FormControl(''),
      member_name: new FormControl(''),

      product_id: new FormControl(''),
      product_name: new FormControl(''),

      discount: new FormControl(''),
      used: new FormControl(''),

    });
    return form
  }

  createForm() {
    let form: FormGroup = this.fb.group({

      id: new FormControl(''),
      name: new FormControl('', [Validators.required]),
      sponsor_id: new FormControl('', [Validators.required]),
      product_id: new FormControl('', [Validators.required]),
      discount: new FormControl('', [Validators.required]),
      qty: new FormControl('', [Validators.required]),
      start: new FormControl('')

    });
    return form
  }

  sponsorSelected(id) {

    this.current_sponsor = this.sponsors.find((e) => {
      return e.id == id
    })

    if (this.current_sponsor && this.current_product) {
      this.loadVouchers()
    }

  }

  productSelected(id) {

    this.current_product = this.products.find((e) => {
      return e.id == id
    })

    if (this.current_sponsor && this.current_product) {
      this.loadVouchers()
    }

  }

  loadVouchers() {

    this._voucherService.selectVouchers(this.current_sponsor.id, this.current_product.id)
      .subscribe((vouchers) => {

        let array: Voucher[] = []

        vouchers.forEach(element => {

          let entity = new Voucher();

          entity.id = element.payload.doc.id;
          entity.name = element.payload.doc.data().name;

          entity.discount = element.payload.doc.data().discount;
          entity.used = element.payload.doc.data().used;

          if (element.payload.doc.data().date_sent) {
            let dt: any = element.payload.doc.data().date_sent;
            entity.date_sent = new Date(dt.seconds * 1000)
          }

          entity.member_id = element.payload.doc.data().member_id;
          entity.member_name = element.payload.doc.data().member_name;
          entity.member_email = element.payload.doc.data().member_email;

          entity.product_id = element.payload.doc.data().product_id;
          entity.product_name = element.payload.doc.data().product_name;

          entity.sponsor_id = element.payload.doc.data().sponsor_id;
          entity.sponsor_name = element.payload.doc.data().sponsor_name;

          array.push(entity)

        });

        this.voucherDataSource = new MatTableDataSource(array);

      })
  }

  createVouchers() {


    if (this.entityForm.valid) {

      this.vouchers = []
      this.dataSource = new MatTableDataSource(this.vouchers);

      const qty = this.entityForm.value.qty;

      let start: number;
      if (this.entityForm.value.start)
        start = Number(this.entityForm.value.start - 1)
      else
        start = 0

      for (let i = 1; i <= qty; i++) {

        let voucher: Voucher = new Voucher()
        voucher.name = this.entityForm.value.name + (i + start).toString()
        voucher.discount = this.entityForm.value.discount;
        voucher.used = false;

        voucher.member_id = "";
        voucher.member_name = "";
        voucher.member_email = "";

        voucher.sponsor_id = this.current_sponsor.id
        voucher.sponsor_name = this.current_sponsor.name

        voucher.product_id = this.current_product.id
        voucher.product_name = this.current_product.name

        this.vouchers.push(voucher)

      }

      // console.log(JSON.stringify(this.vouchers))

      this.dataSource = new MatTableDataSource(this.vouchers);
      this.dataSource.sort = this.sort;

      this.hasList = true;


    }

  }

  registerVouchers() {

    let count: number = 0
    this.vouchers.forEach((voucher) => {
      // console.log(JSON.stringify(voucher))

      this._voucherService.insert(voucher)
        .then(() => {
          count += 1
          if (count >= this.vouchers.length) {
            this.snackBar.openSnackBar("Sucesso!", '', 'success');
          }
        })
        .catch((error) => {
          console.log("Erro." + JSON.stringify(error))
        })
    })

  }

  openDialog(entity) {

    let data: Voucher = new Voucher();

    if (entity) {
      data = entity;
    }

    this.dialog.open(VoucherDialogComponent, {
      width: '800px',
      height: '600px',
      panelClass: 'custom-dialog-container',
      data: {
        'voucher': data,
        'sponsor': this.current_sponsor,
        'product': this.current_product
      }
    });
  }

  delete(element) {
    let dialogRef = this.dialog.open(ConfirmComponent, {
      width: '350px',
      height: '270px',
      panelClass: 'custom-dialog-container',
      data: { title: "Confirme", message: element.name + "<br><br>Excluir este voucher?" }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result == "ok") {

        this._voucherService.delete(element.id)
          .then(
            () => {
              this.snackBar.openSnackBar("Sucesso!", '', 'success')
            }
          )
          .catch(
            (error) => { console.log("delete voucher error: " + error.message) }
          )

      }
    })
  }



}
